import React, { useEffect, useState } from 'react';
import { addProduct, patchProduct } from '../services/ProductService';
import { UNITS, unitCodeFor, formatEur } from '../utils/salesPipeline';
import { useToast } from '../context/UIContext';
import { extractApiError } from '../utils/apiError';
import '../styles/ProjectAcquisition.css';

const VAT_OPTIONS = [0, 7, 19];

const EMPTY = {
    name: '', sku: '', category: '', description: '',
    unit: 'Stk', listPrice: '', vatRate: 19, isActive: true,
};

export default function ProductModal({ product, onClose, onSave }) {
    const toast = useToast();
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm(product
            ? {
                ...EMPTY,
                ...product,
                sku: product.sku ?? '',
                category: product.category ?? '',
                description: product.description ?? '',
                listPrice: product.listPrice ?? '',
            }
            : EMPTY);
        setErrors({});
    }, [product]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = () => {
        const e = {};
        if (!form.name.trim()) e.name = 'Pflichtfeld';
        if (form.listPrice !== '' && parseFloat(form.listPrice) < 0) e.listPrice = 'Preis darf nicht negativ sein.';
        if (Object.keys(e).length) { setErrors(e); return; }

        const payload = {
            ...form,
            name: form.name.trim(),
            sku: form.sku.trim(),
            category: form.category.trim(),
            listPrice: parseFloat(form.listPrice) || 0,
            vatRate: parseFloat(form.vatRate) || 0,
            unitCode: unitCodeFor(form.unit),
        };

        setSaving(true);
        (product ? patchProduct(product.id, payload) : addProduct(payload))
            .then(onSave)
            .catch(err => toast(extractApiError(err, 'Speichern fehlgeschlagen.')))
            .finally(() => setSaving(false));
    };

    const gross = (parseFloat(form.listPrice) || 0) * (1 + (parseFloat(form.vatRate) || 0) / 100);

    return (
        <div className="modal-overlay">
            <div className="modal modal-wide">
                <div className="modal-header">
                    <h3>{product ? 'Produkt bearbeiten' : 'Neues Produkt'}</h3>
                    <button className="modal-close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body">
                    <div className="form-grid-2">
                        <div className="form-field">
                            <label className="form-label">Bezeichnung<span className="form-required">*</span></label>
                            <input
                                type="text" name="name" value={form.name} onChange={handleChange}
                                className={`input${errors.name ? ' input-error' : ''}`}
                                placeholder="z.B. Sensor-Kit S200"
                            />
                            {errors.name && <span className="form-error-msg">{errors.name}</span>}
                        </div>
                        <div className="form-field">
                            <label className="form-label">Artikelnummer / SKU</label>
                            <input
                                type="text" name="sku" value={form.sku} onChange={handleChange}
                                className="input" placeholder="z.B. SNS-S200"
                            />
                        </div>
                    </div>

                    <div className="form-section-label">Preis &amp; Einheit</div>
                    <div className="form-grid-3">
                        <div className="form-field">
                            <label className="form-label">Einheit</label>
                            <select name="unit" value={form.unit} onChange={handleChange} className="input">
                                {UNITS.map(u => (
                                    <option key={u.unit} value={u.unit}>{u.label} ({u.unit})</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-field">
                            <label className="form-label">Listenpreis netto (€)</label>
                            <input
                                type="number" name="listPrice" min={0} step="0.01"
                                value={form.listPrice} onChange={handleChange}
                                className={`input${errors.listPrice ? ' input-error' : ''}`}
                                placeholder="0,00"
                            />
                            {errors.listPrice && <span className="form-error-msg">{errors.listPrice}</span>}
                        </div>
                        <div className="form-field">
                            <label className="form-label">MwSt.-Satz</label>
                            <select name="vatRate" value={form.vatRate} onChange={handleChange} className="input">
                                {VAT_OPTIONS.map(v => <option key={v} value={v}>{v} %</option>)}
                            </select>
                        </div>
                    </div>
                    <p className="acq-hint">
                        Bruttopreis: <strong>{formatEur(gross, { decimals: 2 })}</strong> pro {form.unit}
                    </p>

                    <div className="form-section-label">Einordnung</div>
                    <div className="form-grid-2">
                        <div className="form-field">
                            <label className="form-label">Kategorie</label>
                            <input
                                type="text" name="category" value={form.category} onChange={handleChange}
                                className="input" placeholder="z.B. Hardware, Lizenz, Service"
                            />
                        </div>
                        <div className="form-field">
                            <label className="form-label">Status</label>
                            <label className="acq-checkbox">
                                <input
                                    type="checkbox" name="isActive"
                                    checked={form.isActive !== false} onChange={handleChange}
                                />
                                Aktiv — im Verkauf auswählbar
                            </label>
                        </div>
                    </div>

                    <div className="form-field">
                        <label className="form-label">Beschreibung</label>
                        <textarea
                            name="description" value={form.description} onChange={handleChange}
                            className="input input-textarea" rows={3}
                            placeholder="Leistungsumfang, technische Details, Lieferzeit …"
                        />
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="button" onClick={handleSubmit} disabled={saving}>
                        {saving ? 'Speichern…' : 'Speichern'}
                    </button>
                    <button className="button button-secondary" onClick={onClose}>Abbrechen</button>
                </div>
            </div>
        </div>
    );
}
